<template>
    <div class="ppv2-overflow-x-auto ppv2-pb-6">
        <div v-if="loading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20">
            <div class="ppv2-text-center">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-12 ppv2-w-12 ppv2-border-b-4 ppv2-border-purple-600 ppv2-mx-auto"
                ></div>
                <span class="ppv2-mt-4 ppv2-block ppv2-text-base ppv2-text-black ppv2-opacity-60 poppinsMedium">{{
                    $t('quotes.kanban.loading_board')
                }}</span>
            </div>
        </div>

        <div v-else :key="boardKey" class="ppv2-flex ppv2-flex-col ppv2-gap-8">
            <div class="ppv2-flex ppv2-gap-6 ppv2-min-w-max">
                <div v-for="column in columns" :key="column.id" class="ppv2-w-[15%] ppv2-flex-shrink-0">
                    <!-- Column Content -->
                    <div
                        ref="columnContent"
                        :data-status="column.id"
                        class="ppv2-relative ppv2-rounded-2xl ppv2-p-3 ppv2-min-h-[600px] ppv2-flex ppv2-flex-col ppv2-gap-4 kanban-column"
                        :class="[
                            column.bgClass || 'ppv2-bg-black ppv2-bg-opacity-50',
                            { 'dragged-over': draggedOverColumnId === column.id },
                        ]"
                        :style="
                            draggedOverColumnId === column.id
                                ? { borderColor: column.borderColor, borderStyle: 'dashed', borderWidth: '2px' }
                                : {}
                        "
                    >
                        <!-- Column Header -->
                        <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-mb-1 ppv2-px-2">
                            <h3 class="poppinsMedium ppv2-text-base ppv2-text-black ppv2-opacity-90 !ppv2-mb-0">
                                {{ column.label }}
                            </h3>
                            <div
                                :class="[
                                    column.badgeClass,
                                    'ppv2-w-[26px] ppv2-h-[26px] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-rounded-full ppv2-text-xs poppinsMedium',
                                ]"
                            >
                                {{ getColumnQuotes(column.id).length }}
                            </div>
                        </div>
                        <div
                            v-for="quote in getColumnQuotes(column.id)"
                            :key="quote.resource + '-' + quote.id"
                            class="kanban-card"
                            :data-quote-id="quote.id"
                            :data-resource="quote.resource"
                        >
                            <devis-kanban-card
                                :quote="quote"
                                @open-detail="$emit('open-answer-detail', quote.id)"
                                @reload="fetchDevis"
                            />
                        </div>

                        <!-- Empty State -->
                        <div
                            v-if="getColumnQuotes(column.id).length === 0"
                            class="posAbs ppv2-flex-grow ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center ppv2-text-black ppv2-text-opacity-90 ppv2-opacity-40"
                        >
                            <span class="ppv2-text-sm">{{ $t('quotes.kanban.empty') }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination Toolbar -->
            <div v-if="meta.current_page < meta.last_page" class="ppv2-flex ppv2-justify-center ppv2-mt-4">
                <button
                    @click="loadMore"
                    :disabled="loadingMore"
                    class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-px-6 ppv2-py-3 ppv2-bg-white ppv2-border ppv2-border-black ppv2-bg-opacity-20 ppv2-rounded-xl ppv2-shadow-sm hover:ppv2-bg-black hover:ppv2-bg-opacity-50 ppv2-transition-all"
                >
                    <span
                        v-if="loadingMore"
                        class="ppv2-animate-spin ppv2-h-4 ppv2-w-4 ppv2-border-2 ppv2-border-black ppv2-border-opacity-30 ppv2-border-t-purple-600 ppv2-rounded-full"
                    ></span>
                    <span class="poppinsMedium ppv2-text-black ppv2-opacity-70">
                        {{ loadingMore ? $t('quotes.kanban.loading') : $t('quotes.kanban.load_more') }}
                    </span>
                </button>
            </div>
        </div>

        <ExperienceDatesModal
            :show="showExpDatesModal"
            :experiences="undatedExperiences"
            :subtitle="expModalSubtitle"
            :order-id="Number(expModalQuote && expModalQuote.order_id) || null"
            @close="showExpDatesModal = false"
            @confirm="retryPendingTransition"
        />
    </div>
</template>

<script>
import Sortable from 'sortablejs'
import DevisKanbanCard from './DevisKanbanCard.vue'
import ExperienceDatesModal from './ExperienceDatesModal.vue'

export default {
    name: 'DevisKanban',
    components: {
        DevisKanbanCard,
        ExperienceDatesModal,
    },
    props: {
        startDate: {
            type: String,
            required: true,
        },
        endDate: {
            type: String,
            required: true,
        },
        typeFilter: {
            type: String,
            default: 'all',
        },
        searchQuery: {
            type: String,
            default: '',
        },
        filters: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            quotes: [],
            loading: false,
            loadingMore: false,
            // Drag & drop : instances SortableJS (une par colonne) + clé de re-render.
            // boardKey est incrémenté pour effacer les mutations DOM physiques de SortableJS
            // et laisser Vue réaligner son Virtual DOM sur le tableau `quotes` (cf. DataTable.vue).
            sortables: [],
            boardKey: 0,
            processingDrop: false,
            draggedOverColumnId: null,
            meta: {
                current_page: 1,
                last_page: 1,
            },
            perPage: 100,
            // Modale « expériences sans date » : bloque un devis à créneau non daté avant
            // conversion (garde serveur QuoteExperienceDateGuard → 400 « gagné » / 422 « facturé »).
            showExpDatesModal: false,
            undatedExperiences: [],
            expModalQuote: null,
            // Transition drag & drop bloquée par le garde, à rejouer une fois les dates saisies.
            pendingTransition: null,
        }
    },
    computed: {
        isDark() {
            return this.theme.mode === 'dark'
        },
        expModalSubtitle() {
            const q = this.expModalQuote
            if (!q) return ''
            const ref = q.quote_serial_number || q.order_id || 'N/A'
            const name = q.customer_name || q.client_name || ''
            return name ? `#${ref} · ${name}` : `#${ref}`
        },
        columns() {
            return [
                {
                    id: 'Brouillon',
                    label: this.$t('quotes.kanban.columns.brouillon'),
                    badgeClass: 'ppv2-bg-[#9C9C9C] ppv2-text-white',
                    bgClass: 'bg-brouillon',
                    borderColor: '#9C9C9C',
                },
                {
                    id: 'Demande',
                    label: this.$t('quotes.kanban.columns.demande'),
                    badgeClass: 'ppv2-bg-[#1F3CCD] ppv2-text-white',
                    bgClass: 'bg-demande',
                    borderColor: '#1F3CCD',
                },
                {
                    id: 'Envoyé',
                    label: this.$t('quotes.kanban.columns.envoye'),
                    badgeClass: 'ppv2-bg-[#0075FF] ppv2-text-white',
                    bgClass: 'bg-envoye',
                    borderColor: '#0075FF',
                },
                {
                    id: 'Expiré',
                    label: this.$t('quotes.kanban.columns.expire'),
                    badgeClass: 'ppv2-bg-[#DBA800] ppv2-text-white',
                    bgClass: 'bg-expire',
                    borderColor: '#DBA800',
                },
                {
                    id: 'Gagné',
                    label: this.$t('quotes.kanban.columns.gagne'),
                    badgeClass: 'ppv2-bg-[#088F4C] ppv2-text-white',
                    bgClass: 'bg-gagne',
                    borderColor: '#088F4C',
                },
                {
                    id: 'Perdu',
                    label: this.$t('quotes.kanban.columns.perdu'),
                    badgeClass: 'ppv2-bg-[#B3001B] ppv2-text-white',
                    bgClass: 'bg-perdu',
                    borderColor: '#B3001B',
                },
            ]
        },
    },
    watch: {
        startDate() {
            this.reload()
        },
        endDate() {
            this.reload()
        },
        typeFilter() {
            this.reload()
        },
        // Toute reconstruction du board (changement de clé) doit rebrancher le drag & drop
        // sur les nouveaux noeuds DOM des colonnes, sinon le glissement se fige après un drop.
        boardKey() {
            this.$nextTick(this.setupSortables)
        },
    },
    mounted() {
        this.fetchDevis()
    },
    beforeDestroy() {
        this.destroySortables()
    },
    methods: {
        async fetchDevis(loadMore = false) {
            if (loadMore) {
                this.loadingMore = true
            } else {
                this.loading = true
                this.meta.current_page = 1
            }

            try {
                const response = await this.axios.get('/back-office/devis-v3/list', {
                    params: {
                        startDate: this.startDate,
                        endDate: this.endDate,
                        search: this.searchQuery,
                        typeFilter: this.typeFilter,
                        page: this.meta.current_page,
                        perPage: this.perPage,
                        ...this.filters,
                    },
                })

                const data = response.data || response
                const newQuotes = data.data || []

                if (loadMore) {
                    this.quotes = [...this.quotes, ...newQuotes]
                } else {
                    this.quotes = newQuotes
                }

                this.meta.current_page = parseInt(data.meta?.current_page) || 1
                this.meta.last_page = parseInt(data.meta?.last_page) || 1
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('quotes.kanban.load_error'))
            } finally {
                this.loading = false
                this.loadingMore = false
                // Le board vient (ré)apparaître : (re)brancher le drag & drop sur les colonnes.
                this.$nextTick(this.setupSortables)
            }
        },
        loadMore() {
            if (this.meta.current_page < this.meta.last_page) {
                this.meta.current_page++
                this.fetchDevis(true)
            }
        },
        getColumnQuotes(status) {
            return this.quotes.filter((q) => q.status_label === status)
        },
        reload() {
            this.fetchDevis()
        },
        handleSearchUpdate(searchValue) {
            this.fetchDevis()
        },

        /* ------------------------------------------------------------------ *
         *  Drag & drop
         *  Le glisser-déposer ne fait que déclencher EXACTEMENT les mêmes
         *  transitions/endpoints que les boutons de DevisKanbanCard.vue.
         *  Toute combinaison non prévue est annulée (la carte revient à sa
         *  place) : aucune nouvelle transition n'est introduite côté drag.
         * ------------------------------------------------------------------ */
        destroySortables() {
            this.sortables.forEach((s) => s.destroy())
            this.sortables = []
        },
        setupSortables() {
            this.destroySortables()

            const refs = this.$refs.columnContent
            if (!refs) return
            const columns = Array.isArray(refs) ? refs : [refs]

            columns.forEach((el) => {
                if (!el) return
                const columnStatus = el.dataset.status
                this.sortables.push(
                    Sortable.create(el, {
                        group: 'devis-kanban',
                        draggable: '.kanban-card',
                        // Les boutons/menus restent cliquables : on n'initie pas de drag dessus.
                        filter: 'button, a',
                        preventOnFilter: false,
                        animation: 150,
                        ghostClass: 'ppv2-opacity-40',
                        dragClass: 'ppv2-shadow-lg',
                        onStart: () => {
                            this.draggedOverColumnId = columnStatus
                        },
                        onEnd: (evt) => {
                            this.draggedOverColumnId = null
                            this.onDrop(evt)
                        },
                        onMove: (evt) => {
                            this.draggedOverColumnId = evt.to?.dataset?.status
                        },
                    })
                )
            })
        },
        onDrop(evt) {
            const fromStatus = evt.from?.dataset?.status
            const toStatus = evt.to?.dataset?.status

            // Réordonnancement dans la même colonne : non persisté → on annule le déplacement.
            if (!toStatus || fromStatus === toStatus) {
                this.boardKey++
                return
            }

            const quoteId = evt.item?.dataset?.quoteId
            const resource = evt.item?.dataset?.resource
            const quote = this.quotes.find((q) => String(q.id) === String(quoteId) && q.resource === resource)

            const transition = this.resolveTransition(quote, fromStatus, toStatus)
            if (!transition || this.processingDrop) {
                // Transition non autorisée (ou drop déjà en cours) → la carte revient en place.
                this.boardKey++
                return
            }

            this.applyTransition(quote, transition)
        },
        // Reproduit la matrice de transitions de DevisKanbanCard.vue (boutons + menu).
        resolveTransition(quote, from, to) {
            if (!quote) return null
            const isFinal = (s) => ['Gagné', 'Perdu'].includes(s)

            // Un devis expiré ne peut pas être marqué comme gagné.
            if (from === 'Expiré' && to === 'Gagné') {
                return null
            }

            // Un brouillon ne peut pas être gagné : le client ne l'a jamais reçu. Il doit
            // d'abord passer par « Envoyé ». Le serveur refuse déjà la transition
            // (DevisController::validateQuoteApi) ; on ne la propose simplement plus,
            // pour que la carte revienne en place au lieu de partir chercher une erreur.
            if (from === 'Brouillon' && to === 'Gagné') {
                return null
            }

            // markAsWon / markAsLost : uniquement les devis "invoice" non finalisés.
            if (quote.resource === 'invoice' && quote.order_id && !isFinal(from)) {
                if (to === 'Gagné') return { type: 'won' }
                if (to === 'Perdu') return { type: 'lost' }
            }
            // Demande → Brouillon (respondToRequest)
            if (from === 'Demande' && to === 'Brouillon') return { type: 'respond' }
            // Brouillon → Envoyé (sendQuote)
            if (from === 'Brouillon' && to === 'Envoyé' && quote.order_id) return { type: 'send' }
            // Perdu → (réouverture, le backend repositionne le statut)
            if (from === 'Perdu' && !isFinal(to) && quote.order_id) return { type: 'reopen' }

            return null
        },
        async applyTransition(quote, transition) {
            this.processingDrop = true
            // On efface d'abord le déplacement physique de SortableJS (la carte revient dans
            // sa colonne d'origine) pour réaligner le DOM sur le Virtual DOM de Vue AVANT tout
            // autre re-render. Sans ça, le toggle `loading` de fetchDevis patche un DOM desyncé
            // et le drag & drop se fige. fetchDevis() repositionnera ensuite la carte.
            this.boardKey++
            await this.$nextTick()
            try {
                switch (transition.type) {
                    case 'won':
                        await this.axios.post(`/back-office/devis/${quote.order_id}/validate-api`)
                        this.$toast.success(this.$t('quotes.card.toasts.marked_won'))
                        break
                    case 'lost':
                        await this.axios.post(`/back-office/devis/${quote.order_id}/refuse`)
                        this.$toast.success(this.$t('quotes.card.toasts.marked_lost'))
                        break
                    case 'respond':
                        if (quote.resource === 'answer') {
                            await this.axios.post(`/back-office/devis-v3/answer/${quote.id}/respond`)
                        } else {
                            await this.axios.post(`/back-office/devis/${quote.order_id}/to-draft`)
                        }
                        this.$toast.success(this.$t('quotes.card.toasts.draft_created'))
                        break
                    case 'send':
                        await this.axios.get(`/back-office/devis/${quote.order_id}/sent-quote`)
                        this.$toast.success(this.$t('quotes.card.toasts.sent'))
                        break
                    case 'reopen':
                        await this.axios.post(`/back-office/devis/${quote.order_id}/reopen`)
                        this.$toast.success(this.$t('quotes.card.toasts.reopened'))
                        break
                }
            } catch (error) {
                // Expériences à créneau sans date : « gagné » renvoie 400 (validate-api),
                // « facturer » renverrait 422 — dans les deux cas on ouvre la modale de saisie
                // des dates au lieu d'un simple toast d'erreur.
                const undated = error.response?.data?.errors?.undated_experiences || error.response?.data?.undated_experiences
                if ([400, 422].includes(error.response?.status) && Array.isArray(undated) && undated.length) {
                    this.openExpDatesModal(undated, quote, transition)
                } else {
                    this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
                }
            } finally {
                this.processingDrop = false
                // fetchDevis() resynchronise les colonnes avec le backend et reconstruit
                // le board (re-render) → efface aussi le déplacement physique de SortableJS.
                this.fetchDevis()
            }
        },
        openExpDatesModal(list, quote, transition) {
            this.expModalQuote = quote
            this.pendingTransition = transition
            // Champs enrichis conservés (item_id, slug, ticket_selections…) : la modale rouvre
            // le sélecteur de créneau sur place, sans recharger la ligne.
            // On PRÉSERVE la date renvoyée par le serveur : les lignes déjà datées s'affichent
            // en vert (lecture seule), seules les non datées (date null) portent l'action.
            this.undatedExperiences = list.map((e) => ({ ...e, date: e.date ?? null }))
            this.showExpDatesModal = true
        },
        // Toutes les lignes datées sur place : on rejoue la transition qui avait été bloquée
        // par le garde (aucune navigation), qui doit désormais passer.
        retryPendingTransition() {
            this.showExpDatesModal = false
            const quote = this.expModalQuote
            const transition = this.pendingTransition
            this.pendingTransition = null
            if (quote && transition) {
                this.applyTransition(quote, transition)
            }
        },
    },
}
</script>

<style scoped>
.kanban-column {
    min-height: calc(100vh - 400px);
    transition: all 0.3s ease;
    border: 1px solid transparent;
}
.kanban-card {
    cursor: grab;
}
.kanban-card:active {
    cursor: grabbing;
}
.bg-brouillon {
    background-color: #eef0f2;
}
.dark .bg-brouillon {
    background-color: rgba(255, 255, 255, 0.12);
}

.bg-demande {
    background-color: #e9eafb;
}
.dark .bg-demande {
    background-color: #1f3ccd1f;
}

.bg-envoye {
    background-color: #e1ecf8;
}
.dark .bg-envoye {
    background-color: #0075ff1f;
}

.bg-expire {
    background-color: #fbf2da;
}
.dark .bg-expire {
    background-color: #dba8001f;
}

.bg-gagne {
    background-color: #dcf1e6;
}
.dark .bg-gagne {
    background-color: #088f4c1f;
}

.bg-perdu {
    background-color: #f8e1e7;
}
.dark .bg-perdu {
    background-color: #b3001b1f;
}
.posAbs {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
</style>
